import React, { useState } from 'react';
import { ethers } from 'ethers';
import Modal from '../common/Modal';
import MiniLoader from '../common/MiniLoader';
import { PACKAGE_PRICES } from '../../config/contracts';
import { CONTRACTS } from '../../config/contracts';
import { useContracts } from '../../hooks/useContract';
import { useUsdtBalance } from '../../hooks/useUsdtBalance';
import { useNotify } from '../../context/NotificationContext';
import { parseError } from '../../utils/errors';
import { formatUsdt } from '../../utils/format';

const ActivatePackage = ({ packageId, onClose, onActivated }) => {
  const { logic, usdt } = useContracts();
  const { balance, allowance, refresh } = useUsdtBalance();
  const notify = useNotify();

  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(''); // '', 'approving', 'activating'

  const price = PACKAGE_PRICES[packageId - 1];
  const priceBn = ethers.utils.parseUnits(String(price), 18);
  const needsApproval = allowance.lt(priceBn);
  const insufficientBalance = balance.lt(priceBn);

  // ONE-CLICK: approve (if needed) then activate, in a single flow.
  const activate = async () => {
    setBusy(true);
    try {
      // Step 1 — approve if allowance insufficient
      if (needsApproval) {
        setStep('approving');
        const approveLoad = notify.loading('Approving USDT (one-time)...');
        try {
          const approveTx = await usdt.approve(CONTRACTS.GlobalStorage, ethers.constants.MaxUint256);
          notify.dismiss(approveLoad);
          const mining = notify.loading('Confirming approval...');
          await approveTx.wait();
          notify.dismiss(mining);
          await refresh();
        } catch (err) {
          notify.dismiss(approveLoad);
          notify.error(parseError(err));
          setBusy(false);
          setStep('');
          return;
        }
      }

      // Step 2 — activate the package
      setStep('activating');
      const actLoad = notify.loading(`Activating Package ${packageId}...`);
      try {
        const tx = await logic.activatePackage(packageId);
        notify.dismiss(actLoad);
        const mining = notify.loading('Confirming...');
        await tx.wait();
        notify.dismiss(mining);
        notify.success(`Package ${packageId} activated!`);
        if (onActivated) onActivated();
      } catch (err) {
        notify.dismiss(actLoad);
        notify.error(parseError(err));
      }
    } finally {
      setBusy(false);
      setStep('');
    }
  };

  return (
    <Modal isOpen={true} onClose={busy ? undefined : onClose} title={`Activate Package ${packageId}`}>
      <div className="text-center mb-lg">
        <div className="package-price" style={{ fontSize: 52 }}>${price}</div>
        <div className="text-muted mt-xs" style={{ letterSpacing: 2, fontSize: 12 }}>USDT</div>
      </div>

      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div className="flex flex-between">
          <span className="text-muted" style={{ fontSize: 13 }}>Your USDT Balance</span>
          <span style={{ fontSize: 14, fontWeight: 600 }} className={insufficientBalance ? 'text-error' : ''}>
            {formatUsdt(balance)} USDT
          </span>
        </div>
      </div>

      {insufficientBalance && (
        <div className="mb-md">
          <span className="badge badge-error">Insufficient USDT balance</span>
        </div>
      )}

      <div className="flex gap-sm">
        <button className="btn btn-secondary" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button
          className="btn btn-primary btn-lg flex-1"
          onClick={activate}
          disabled={busy || insufficientBalance}
          style={{ fontSize: 16, padding: '18px 24px' }}
        >
          {busy ? (
            <>
              <MiniLoader />
              {step === 'approving' ? 'Approving...' : step === 'activating' ? 'Activating...' : 'Processing...'}
            </>
          ) : (
            <>⚡ Activate for ${price}</>
          )}
        </button>
      </div>

      <div className="text-center mt-md text-muted" style={{ fontSize: 12 }}>
        Ceiling: 5x = ${price * 5} USDT max earnings
      </div>
    </Modal>
  );
};

export default ActivatePackage;
